import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Users, Tag, X } from "lucide-react";
import { toast } from "sonner";

type P = {
  id: string;
  participant_type: string;
  display_name: string | null;
  is_active: boolean;
  connected_at: string;
  speaker_labels: string[] | null;
};

const SPEAKER_OPTIONS = ["Speaker A", "Speaker B", "Speaker C", "Speaker D", "Speaker E"];

export function ParticipantsPanel({ sessionId }: { sessionId: string }) {
  const [items, setItems] = useState<P[]>([]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase
        .from("jarvis_participants")
        .select("id, participant_type, display_name, is_active, connected_at, speaker_labels")
        .eq("session_id", sessionId)
        .eq("is_active", true)
        .order("connected_at", { ascending: true });
      if (!cancelled) setItems((data ?? []) as P[]);
    };
    void load();
    const ch = supabase
      .channel(`jarvis-participants-${sessionId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "jarvis_participants", filter: `session_id=eq.${sessionId}` }, () => { void load(); })
      .subscribe();
    return () => { cancelled = true; void supabase.removeChannel(ch); };
  }, [sessionId]);

  const assignLabel = async (participantId: string, label: string, current: string[] | null) => {
    const next = Array.from(new Set([...(current ?? []), label]));
    const { error } = await supabase
      .from("jarvis_participants")
      .update({ speaker_labels: next })
      .eq("id", participantId);
    if (error) {
      toast.error(`Falha ao atribuir: ${error.message}`);
      return;
    }
    toast.success(`${label} atribuído`);
  };

  const removeLabel = async (participantId: string, label: string, current: string[] | null) => {
    const next = (current ?? []).filter((l) => l !== label);
    const { error } = await supabase
      .from("jarvis_participants")
      .update({ speaker_labels: next })
      .eq("id", participantId);
    if (error) {
      toast.error(`Falha ao remover: ${error.message}`);
    }
  };

  // Coleta labels já atribuídos a outros participantes para não duplicar
  const usedLabels = new Set(
    items.flatMap((p) => p.speaker_labels ?? []),
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="w-4 h-4" /> Participantes
          <Badge variant="secondary" className="ml-auto tabular-nums">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Nenhum participante conectado.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((p) => {
              const labels = p.speaker_labels ?? [];
              const available = SPEAKER_OPTIONS.filter(
                (l) => !labels.includes(l) && !(usedLabels.has(l) && !labels.includes(l)),
              );
              return (
                <li key={p.id} className="flex flex-col gap-1.5 text-sm border-b border-border/30 last:border-0 pb-2 last:pb-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate flex-1">{p.display_name || `Anônimo · ${p.id.slice(0, 6)}`}</span>
                    <Badge variant="outline" className="text-[10px] capitalize shrink-0">{p.participant_type}</Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {labels.map((l) => (
                      <Badge key={l} variant="secondary" className="text-[10px] gap-1 pr-1">
                        {l}
                        <button
                          type="button"
                          onClick={() => void removeLabel(p.id, l, labels)}
                          className="hover:text-destructive"
                          aria-label={`Remover ${l}`}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </Badge>
                    ))}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] gap-1">
                          <Tag className="w-2.5 h-2.5" /> Atribuir
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-44 p-1" align="end">
                        {available.length === 0 ? (
                          <p className="text-[11px] text-muted-foreground p-2 italic">Sem labels livres</p>
                        ) : (
                          available.map((l) => (
                            <Button
                              key={l}
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start h-7 text-xs"
                              onClick={() => void assignLabel(p.id, l, labels)}
                            >
                              {l}
                            </Button>
                          ))
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
